import React, { useState } from 'react';
import { Button, Badge, Alert } from '../ui';

// Mock data for countries
const MOCK_COUNTRIES = [
  {
    id: 1,
    code: 'uk',
    name: 'United Kingdom',
    flag: '🇬🇧',
    status: 'active',
    regions: ['England', 'Wales', 'Scotland', 'Northern Ireland'],
    templates: 3,
    contracts: 1245,
    languages: ['en'],
    currency: 'GBP',
    lastUpdated: '2026-01-20',
  },
  {
    id: 2,
    code: 'de',
    name: 'Germany',
    flag: '🇩🇪',
    status: 'active',
    regions: ['Berlin', 'Bavaria', 'Hamburg', 'North Rhine-Westphalia'],
    templates: 2,
    contracts: 892,
    languages: ['de', 'en'],
    currency: 'EUR',
    lastUpdated: '2026-01-18',
  },
  {
    id: 3,
    code: 'fr',
    name: 'France',
    flag: '🇫🇷',
    status: 'active',
    regions: ['Île-de-France', 'Provence', 'Occitanie'],
    templates: 2,
    contracts: 654,
    languages: ['fr', 'en'],
    currency: 'EUR',
    lastUpdated: '2026-01-15',
  },
  {
    id: 4,
    code: 'es',
    name: 'Spain',
    flag: '🇪🇸',
    status: 'active',
    regions: ['Madrid', 'Catalonia', 'Andalusia', 'Valencia'],
    templates: 2,
    contracts: 523,
    languages: ['es', 'en'],
    currency: 'EUR',
    lastUpdated: '2026-01-12',
  },
  {
    id: 5,
    code: 'it',
    name: 'Italy',
    flag: '🇮🇹',
    status: 'active',
    regions: ['Lombardy', 'Lazio', 'Tuscany', 'Veneto'],
    templates: 2,
    contracts: 412,
    languages: ['it', 'en'],
    currency: 'EUR',
    lastUpdated: '2026-01-10',
  },
  {
    id: 6,
    code: 'us',
    name: 'United States',
    flag: '🇺🇸',
    status: 'coming_soon',
    regions: ['California', 'New York', 'Texas', 'Florida'],
    templates: 0,
    contracts: 0,
    languages: ['en'],
    currency: 'USD',
    lastUpdated: null,
  },
  {
    id: 7,
    code: 'ca',
    name: 'Canada',
    flag: '🇨🇦',
    status: 'coming_soon',
    regions: ['Ontario', 'Quebec', 'British Columbia'],
    templates: 0,
    contracts: 0,
    languages: ['en', 'fr'],
    currency: 'CAD',
    lastUpdated: null,
  },
  {
    id: 8,
    code: 'au',
    name: 'Australia',
    flag: '🇦🇺',
    status: 'development',
    regions: ['New South Wales', 'Victoria', 'Queensland'],
    templates: 1,
    contracts: 0,
    languages: ['en'],
    currency: 'AUD',
    lastUpdated: '2026-01-22',
  },
];

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    active: { color: 'emerald', label: 'Active' },
    coming_soon: { color: 'amber', label: 'Coming Soon' },
    development: { color: 'blue', label: 'In Development' },
    inactive: { color: 'gray', label: 'Inactive' },
  };

  const config = statusConfig[status] || statusConfig.inactive;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
      {config.label}
    </span>
  );
}

// Country card component
function CountryCard({ country, onEdit, onToggleStatus }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-4xl">{country.flag}</span>
          <div>
            <h3 className="font-semibold text-gray-900">{country.name}</h3>
            <p className="text-sm text-gray-500 uppercase">{country.code}</p>
          </div>
        </div>
        <StatusBadge status={country.status} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-2xl font-bold text-gray-900">{country.templates}</p>
          <p className="text-xs text-gray-500">Templates</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-2xl font-bold text-gray-900">{country.contracts.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Contracts</p>
        </div>
      </div>

      {/* Regions */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-2">Regions ({country.regions.length})</p>
        <div className="flex flex-wrap gap-1">
          {country.regions.slice(0, 3).map((region) => (
            <span key={region} className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-600">
              {region}
            </span>
          ))}
          {country.regions.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-600">
              +{country.regions.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Languages & Currency */}
      <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
          <span>{country.languages.join(', ').toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{country.currency}</span>
        </div>
      </div>

      {/* Last Updated */}
      {country.lastUpdated && (
        <p className="text-xs text-gray-400 mb-4">
          Last updated: {new Date(country.lastUpdated).toLocaleDateString()}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(country)}
          className="flex-1 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100 transition-colors"
        >
          Configure
        </button>
        <button
          onClick={() => onToggleStatus(country)}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            country.status === 'active'
              ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
          }`}
        >
          {country.status === 'active' ? 'Disable' : 'Enable'}
        </button>
      </div>
    </div>
  );
}

// Edit Country Modal
function EditCountryModal({ country, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: country?.name || '',
    code: country?.code || '',
    status: country?.status || 'development',
    regions: country?.regions?.join(', ') || '',
    languages: country?.languages?.join(', ') || '',
    currency: country?.currency || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...country,
      ...formData,
      regions: formData.regions.split(',').map(r => r.trim()),
      languages: formData.languages.split(',').map(l => l.trim().toLowerCase()),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">
            {country ? 'Edit Country' : 'Add Country'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                maxLength={2}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="active">Active</option>
              <option value="development">In Development</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Regions (comma separated)</label>
            <textarea
              value={formData.regions}
              onChange={(e) => setFormData({ ...formData, regions: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={2}
              placeholder="e.g., England, Wales, Scotland"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Languages (comma separated)</label>
              <input
                type="text"
                value={formData.languages}
                onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., en, de"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                maxLength={3}
                placeholder="e.g., EUR"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminCountries() {
  const [countries, setCountries] = useState(MOCK_COUNTRIES);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCountry, setEditingCountry] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Filter countries
  const filteredCountries = countries.filter((country) => {
    const matchesFilter = filter === 'all' || country.status === filter;
    const matchesSearch = country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Stats
  const stats = {
    total: countries.length,
    active: countries.filter(c => c.status === 'active').length,
    development: countries.filter(c => c.status === 'development').length,
    comingSoon: countries.filter(c => c.status === 'coming_soon').length,
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setShowModal(true);
  };

  const handleToggleStatus = (country) => {
    setCountries(countries.map(c => {
      if (c.id === country.id) {
        return { ...c, status: c.status === 'active' ? 'inactive' : 'active' };
      }
      return c;
    }));
  };

  const handleSave = (updatedCountry) => {
    setCountries(countries.map(c => c.id === updatedCountry.id ? updatedCountry : c));
    setShowModal(false);
    setEditingCountry(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Countries & Regions</h1>
          <p className="text-gray-600 mt-1">Manage supported countries and their configurations</p>
        </div>
        <button
          onClick={() => { setEditingCountry(null); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Country
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Countries</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.development}</p>
              <p className="text-sm text-gray-500">In Development</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.comingSoon}</p>
              <p className="text-sm text-gray-500">Coming Soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search countries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'development', label: 'Development' },
            { value: 'coming_soon', label: 'Coming Soon' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                filter === option.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Countries Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCountries.map((country) => (
          <CountryCard
            key={country.id}
            country={country}
            onEdit={handleEdit}
            onToggleStatus={handleToggleStatus}
          />
        ))}
      </div>

      {filteredCountries.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No countries found</h3>
          <p className="text-gray-500">Try adjusting your search or filter</p>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && (
        <EditCountryModal
          country={editingCountry}
          onClose={() => { setShowModal(false); setEditingCountry(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
